'use client';

import { type RefObject, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';
import NextImage from 'next/image';
import { useAnalytics } from '@/hooks/useAnalytics';
import { APP_LINK, openAppLink } from '@/lib/app-link';
import { cn } from '@/lib/cn';

/**
 * Routes that suppress the banner.
 *
 * These are focused, task-oriented screens that carry their own app bar with a
 * back affordance. Stacking an undismissable promo strip on top of that pushes
 * the screen's real heading down and reads as a second, competing header.
 *
 * Currently empty. The read-only application view used to be listed here, but
 * it now hides the mweb search header entirely, so the banner is the only
 * chrome above its app bar rather than a second one competing with it.
 */
const BANNER_HIDDEN_ROUTES: RegExp[] = [];

/** How long scrolling must pause before a half-shown banner settles open or closed. */
const SNAP_DELAY_MS = 150;

const SNAP_TIMING = '200ms cubic-bezier(0.2, 0, 0, 1)';

let currentHeight = 0;
const heightListeners = new Set<() => void>();

function publishHeight(next: number) {
  const rounded = Math.round(next);
  if (rounded === currentHeight) return;
  currentHeight = rounded;
  heightListeners.forEach(notify => notify());
}

/**
 * Live height of the banner in px — 0 on desktop, on suppressed routes, and
 * while it is collapsed, and every intermediate value as it scrolls away.
 *
 * The banner sits above the app shell, so collapsing it hands its height back
 * to the shell and everything below shifts up. Layouts that size themselves
 * off the viewport rather than off the shell (the listings bottom sheet) have
 * to subtract this, or they leave a gap where the banner used to be.
 */
export function useSmartAppBannerHeight() {
  return useSyncExternalStore(
    onChange => {
      heightListeners.add(onChange);
      return () => {
        heightListeners.delete(onChange);
      };
    },
    () => currentHeight,
    () => 0,
  );
}

/**
 * Scrolls the banner away pixel-for-pixel with the page and pulls it back the
 * same way on scroll up, like a mobile browser's address bar. If scrolling
 * stops with it half shown, it settles to whichever state is closer.
 *
 * Pages scroll inside the root layout's `<main>` (and a few inside their own
 * nested containers) rather than the window, and the banner has no reference
 * to any of them. So rather than tracking one element, this watches every
 * scroller on the page and keys the last known offset off whichever one fired.
 *
 * Styles are written straight to the DOM rather than through state, so a
 * scroll frame never waits on a React render. State only flips at the fully
 * hidden boundary, to take the strip out of the tab order.
 */
function useScrollLinkedCollapse(
  pathname: string | null,
  stripRef: RefObject<HTMLDivElement | null>,
  contentRef: RefObject<HTMLElement | null>,
) {
  const [isHidden, setIsHidden] = useState(false);

  // Re-runs per route: a fresh route starts at the top of its own content, so
  // the banner starts fully shown.
  useEffect(() => {
    const strip = stripRef.current;
    const content = contentRef.current;
    if (!strip || !content) return;

    const offsets = new WeakMap<Element, { top: number; clientHeight: number }>();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let hidden = 0;
    let lastTop = 0;
    let snapping = false;
    let frame = 0;
    let snapTimer: ReturnType<typeof setTimeout> | undefined;
    let target: EventTarget | null = null;

    const apply = (next: number, animate: boolean) => {
      const full = content.offsetHeight;
      hidden = Math.min(Math.max(next, 0), full);
      snapping = animate;
      strip.style.transition = animate ? `height ${SNAP_TIMING}` : '';
      content.style.transition = animate ? `transform ${SNAP_TIMING}` : '';
      // Left on `auto` at rest so the strip still follows its content's height.
      strip.style.height = hidden || animate ? `${full - hidden}px` : '';
      content.style.transform = hidden ? `translateY(${-hidden}px)` : '';
      setIsHidden(hidden >= full);
    };

    const settle = () => {
      const full = content.offsetHeight;
      if (hidden <= 0 || hidden >= full) return;
      const close = hidden > full / 2 && lastTop >= full;
      apply(close ? full : 0, !reducedMotion.matches);
    };

    const measure = () => {
      frame = 0;
      const scroller =
        target === document || target === window
          ? document.scrollingElement
          : target instanceof Element
            ? target
            : null;
      if (!scroller) return;
      const max = scroller.scrollHeight - scroller.clientHeight;
      // Carousels and filter rails scroll horizontally; they must not move the banner.
      if (max < 1) return;

      // iOS rubber-banding reports offsets past either end; the bounce back
      // must not read as a scroll in the other direction.
      const top = Math.min(Math.max(scroller.scrollTop, 0), max);
      const previous = offsets.get(scroller);
      offsets.set(scroller, { top, clientHeight: scroller.clientHeight });
      lastTop = top;
      const delta = previous ? top - previous.top : 0;

      // Collapsing grows the scroller, and at the bottom the browser clamps
      // scrollTop to fit — which reads as an upward scroll and would pull the
      // banner straight back.
      if (
        delta < 0 &&
        top >= max - 1 &&
        previous &&
        scroller.clientHeight > previous.clientHeight
      ) {
        return;
      }

      // A user scroll interrupting the settle picks up from wherever the
      // animation is, not from where it was headed.
      if (snapping) hidden = content.offsetHeight - strip.getBoundingClientRect().height;

      // Never more hidden than the page is scrolled, so it is fully back by the top.
      apply(Math.min(hidden + delta, top), false);

      clearTimeout(snapTimer);
      snapTimer = setTimeout(settle, SNAP_DELAY_MS);
    };

    const handleScroll = (event: Event) => {
      target = event.target;
      if (!frame) frame = requestAnimationFrame(measure);
    };

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== strip || event.propertyName !== 'height') return;
      apply(hidden, false);
    };

    apply(0, false);
    // Scroll doesn't bubble, so listen on the way down to catch every container.
    document.addEventListener('scroll', handleScroll, true);
    strip.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      clearTimeout(snapTimer);
      document.removeEventListener('scroll', handleScroll, true);
      strip.removeEventListener('transitionend', handleTransitionEnd);
      strip.style.cssText = '';
      content.style.cssText = '';
    };
  }, [pathname, stripRef, contentRef]);

  return isHidden;
}

/**
 * Custom mobile-web "smart app banner". Unlike Apple's native Smart App Banner
 * (the `apple-itunes-app` meta tag, iOS Safari only + easily dismissed), this
 * renders on all mobile browsers and is not dismissible.
 *
 * The entire strip is a single clickable button. It sits in normal document
 * flow at the top of the app shell (so it pushes content down) and is hidden
 * at >=768px via `md:hidden`, so it never shows on desktop. It also opts out
 * of the routes in BANNER_HIDDEN_ROUTES.
 *
 * It shows on load and then scrolls away with the page (see
 * useScrollLinkedCollapse). Because it stays in flow, the fixed mobile headers
 * anchored to the app shell below move up with it, with no changes needed in
 * those components.
 */
export function SmartAppBanner() {
  const pathname = usePathname();
  const analytics = useAnalytics();
  const stripRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLButtonElement>(null);
  const isHidden = useScrollLinkedCollapse(pathname, stripRef, contentRef);

  // Publishes every frame of the collapse, so subscribers track the banner
  // instead of jumping once it lands. Keyed on pathname because the element is
  // absent on suppressed routes, and that is the only thing that suppresses it.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) {
      publishHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => publishHeight(strip.getBoundingClientRect().height));
    observer.observe(strip);
    publishHeight(strip.getBoundingClientRect().height);
    return () => {
      observer.disconnect();
      publishHeight(0);
    };
  }, [pathname]);

  // After the hooks above, so the hook order stays stable across routes.
  if (pathname && BANNER_HIDDEN_ROUTES.some(route => route.test(pathname))) return null;

  const handleClick = () => {
    analytics.track('smart_app_banner', { screen: pathname || '/' });
    openAppLink(APP_LINK);
  };

  return (
    <div
      ref={stripRef}
      aria-hidden={isHidden}
      className={cn(
        'shrink-0 overflow-hidden bg-primary md:hidden',
        isHidden && 'pointer-events-none',
      )}
    >
      <button
        ref={contentRef}
        type="button"
        onClick={handleClick}
        tabIndex={isHidden ? -1 : undefined}
        aria-label="Open the Openigloo app"
        className="flex w-full items-center gap-2 border-b border-secondary bg-primary px-4 py-4 text-left will-change-transform"
      >
        <NextImage
          src="/app-icon.png"
          alt=""
          width={32}
          height={32}
          aria-hidden
          className="h-8 w-8 shrink-0 rounded-lg border border-secondary"
        />
        <div className="flex items-center gap-4 flex-1">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-caption-high text-primary">
              Openigloo NYC Apartment Finder
            </span>
            <span className="block truncate text-xs text-secondary">For the best experience</span>
          </span>
          <span className="shrink-0 rounded-full bg-[#008BFF] px-3 py-1 text-caption-high text-inverted">
            USE APP
          </span>
        </div>
      </button>
    </div>
  );
}

export default SmartAppBanner;
